import { Box, Button, LinearProgress } from "@mui/material";
import {
    DataGrid,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridCellParams,
    GridColDef,
    GridPaginationModel,
    GridRenderCellParams,
    GridRowHeightParams,
    GridRowParams,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridValueFormatterParams,
    MuiEvent,
} from "@mui/x-data-grid";
import React from "react";
import { useTranslation } from "react-i18next";
import ReplayIcon from "@mui/icons-material/Replay";

interface Column {
    renderHeader?: () => JSX.Element;
    renderCell?: (props: GridRenderCellParams) => JSX.Element;
    valueFormatter?: (props: GridValueFormatterParams) => string;
    field: string;
    headerName?: string;
    hideable?: boolean;
    width?: number;
}

interface IProps {
    rows: any[];
    columns: Column[];
    loading?: boolean;
    rowCount: number;
    paginationModel?: GridPaginationModel;
    updatePagination?: (update: GridPaginationModel) => void;
    onRowClick?: (row: GridRowParams<any>) => void;
    onCellClick?: (
        row: GridCellParams,
        event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>,
    ) => void;
    onColumnVisibilityModelChange: (props: { [key: string]: boolean }) => void;
    columnVisibilityModel: { [key: string]: boolean };
    toolbar?: JSX.Element[];
    checkboxSelection?: boolean;
    onRowSelectionModelChange?: (selectionModel: any[]) => void;
    initialColumnVisibility: { [key: string]: boolean };
    reset: () => void;
    showToolbar?: boolean;
    autoHeight?: string[];
    minHeight?: string;
}

const DataTable = (props: IProps) => {
    const { t } = useTranslation();
    const {
        columns,
        rows,
        loading,
        rowCount,
        updatePagination,
        onRowClick,
        onCellClick,
        paginationModel,
        onColumnVisibilityModelChange,
        columnVisibilityModel,
        toolbar,
        checkboxSelection,
        onRowSelectionModelChange,
        reset,
        showToolbar = true,
        autoHeight,
        minHeight,
    } = props;

    const getTogglableColumns = (columns: GridColDef[]) => {
        return columns
            .filter((column) => column.hideable)
            .map((column) => column.field);
    };

    const handleReset = () => {
        if (onColumnVisibilityModelChange !== undefined) {
            const obj: { [key: string]: boolean } = {};
            columns.forEach((column) => {
                obj[column.field] = false;
            });
            onColumnVisibilityModelChange(obj);
            onColumnVisibilityModelChange(props.initialColumnVisibility);
        }
        reset();
    };

    return (
        <DataGrid
            rows={rows}
            columns={
                checkboxSelection
                    ? [
                          {
                              ...GRID_CHECKBOX_SELECTION_COL_DEF,
                              hideable: false,
                          },
                          ...columns.map(
                              (column): GridColDef => ({
                                  ...column,
                                  minWidth: column.width ? column.width : 200,
                                  flex: 1,
                                  sortable: false,
                                  disableColumnMenu: true,
                                  editable: false,
                                  align: "center",
                                  headerAlign: "center",
                              }),
                          ),
                      ]
                    : columns.map(
                          (column): GridColDef => ({
                              ...column,
                              minWidth: column.width ? column.width : 200,
                              flex: 1,
                              sortable: false,
                              disableColumnMenu: true,
                              editable: false,
                              align: "center",
                              headerAlign: "center",
                          }),
                      )
            }
            rowHeight={36}
            onCellClick={onCellClick}
            onRowSelectionModelChange={onRowSelectionModelChange}
            rowSelection={checkboxSelection}
            checkboxSelection={checkboxSelection}
            paginationModel={paginationModel}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            rowCount={rowCount}
            paginationMode="server"
            onPaginationModelChange={updatePagination}
            onRowClick={onRowClick}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            columnVisibilityModel={columnVisibilityModel}
            slots={{
                loadingOverlay: LinearProgress,
                toolbar: showToolbar
                    ? (props: any) => (
                          <GridToolbarContainer {...props}>
                              <GridToolbarColumnsButton
                                  variant="contained"
                                  size="medium"
                              />
                              <Button
                                  variant="contained"
                                  size="medium"
                                  onClick={handleReset}
                              >
                                  <ReplayIcon />
                              </Button>
                              {toolbar?.map((element, index) => (
                                  <Box key={index}>{element}</Box>
                              ))}
                          </GridToolbarContainer>
                      )
                    : toolbar && toolbar?.length > 0
                      ? (props: any) => (
                            <GridToolbarContainer
                                {...props}
                                sx={{ ...props.sx, justifyContent: "center" }}
                            >
                                {toolbar?.map((element, index) => (
                                    <Box key={index}>{element}</Box>
                                ))}
                            </GridToolbarContainer>
                        )
                      : null,
            }}
            slotProps={{
                columnsPanel: {
                    getTogglableColumns,
                },
                toolbar: {
                    sx: { backgroundColor: "#ececec" },
                },
            }}
            sx={{
                height: minHeight ? minHeight : "100%",
                borderRadius: 0,
                border: 0,
                "& .MuiDataGrid-root": {
                    borderRadius: "0",
                },
                "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#ececec",
                    borderRadius: 0,
                },
                "& .MuiDataGrid-columnHeader": {
                    borderRadius: 0,
                    backgroundColor: "#ececec",
                },
                "& .MuiDataGrid-footerContainer": {
                    backgroundColor: "#ececec",
                },
                "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                    outline: "none !important",
                },
                "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within": {
                    outline: "none !important",
                },
                "& .MuiDataGrid-cell": {
                    whiteSpace: "normal !important",
                },
                "& .MuiDataGrid-row": {
                    minHeight: "36px !important",
                },
            }}
            loading={loading}
            localeText={{
                noRowsLabel: t("columnPanel.noRows"),
                footerRowSelected: (count: number) =>
                    `${count} ${t("pagination.selectedRow")}`,
                MuiTablePagination: {
                    labelRowsPerPage: t("pagination.rowsPerPage"),
                },
                toolbarColumns: t("columnButton"),
                columnsPanelTextFieldLabel: t("columnPanel.textFieldLabel"),
                columnsPanelTextFieldPlaceholder: t(
                    "columnPanel.textFieldLabel",
                ),
                columnsPanelShowAllButton: t("columnPanel.showAllButton"),
                columnsPanelHideAllButton: t("columnPanel.hideAllButton"),
            }}
            getRowHeight={(params: GridRowHeightParams) => {
                const { model } = params;
                if (
                    autoHeight &&
                    autoHeight.length > 0
                    // model.hasOwnProperty(autoHeight) &&
                    // model[autoHeight].length > 1
                )
                    if (
                        autoHeight.some(
                            (height) =>
                                model.hasOwnProperty(height) &&
                                model[height].length > 1,
                        )
                    ) {
                        return "auto";
                    }
                return 36;
            }}
        />
    );
};

export default DataTable;
